/** Financial settings API.
 *
 * The screen this feeds exists so that nobody has to guess which account is
 * cash and which is inventory. The server decides what is still missing and
 * whether financial answers may render at all; this layer must not re-derive
 * either of those, or the screen and the gate will drift apart.
 */

export type StandardLine =
  | 'cash_and_banks'
  | 'receivables'
  | 'inventory'
  | 'other_current_assets'
  | 'non_current_assets'
  | 'payables'
  | 'short_term_debt'
  | 'other_current_liabilities'
  | 'non_current_liabilities'
  | 'equity'
  | 'revenue'
  | 'cogs'
  | 'opex'
  | 'other_income'
  | 'other_expense'
  | 'excluded';

export interface ReadinessCheck {
  id: string;
  label: string;
  passed: boolean;
  detail: string;
}

export interface Question {
  for: 'customer' | 'internal' | 'both';
  kind:
    | 'ledger_imbalance'
    | 'unclassified_account'
    | 'statement_crossing'
    | 'needs_approval';
  text: string;
  account_code?: string;
  account_name?: string | null;
  account_group?: string | null;
  balance?: number | null;
}

export interface ChartAccount {
  code: string;
  name: string | null;
  name_alt: string | null;
  group: string | null;
  parent: string | null;
  level2: string | null;
  depth: number | null;
  is_leaf: boolean;
  /** Resolved through the map — null means nothing classifies this account. */
  line: StandardLine | null;
  balance: number;
}

export interface Ruling {
  account_code: string;
  scope: 'subtree' | 'account';
  standard_line: StandardLine;
  source?: 'auto' | 'confirmed' | 'override';
  note?: string | null;
}

export interface Approval {
  map_fingerprint: string;
  approved_by: string;
  approved_role: 'customer' | 'internal';
  approved_at: string;
  note?: string | null;
}

export interface FinancialSettings {
  available: boolean;
  reason?: string;
  /** True when nothing is stored yet and these rulings are the draft. */
  drafted: boolean;
  ok: boolean;
  checks: ReadinessCheck[];
  questions: Question[];
  basis: string;
  financial_years: number[];
  financial_year: number | null;
  /** False until journal_summary has been re-pulled with closing entries
   *  labelled. Prior-year figures are wrong before that, so the screen says so
   *  rather than showing them. */
  prior_years_reliable: boolean;
  standard_lines: StandardLine[];
  accounts: ChartAccount[];
  rulings: Ruling[];
  approval: Approval | null;
  map_fingerprint: string;
  /** What moved since the sign-off. Empty while the sign-off still holds. */
  changes_since_approval?: MapChange[];
}

export interface MapChange {
  account_code: string;
  scope: 'subtree' | 'account';
  old_line: StandardLine | null;
  new_line: StandardLine | null;
  changed_by: string | null;
  changed_at: string;
}

export interface MetricValue {
  label: string;
  value?: number;
  unit?: 'money' | 'percent' | 'ratio' | 'days';
  good?: 'up' | 'down';
  basis?: string;
  note?: string | null;
  unavailable?: string;
}

export interface FinancialMetrics {
  ok: boolean;
  reason?: string;
  checks?: ReadinessCheck[];
  basis?: string;
  financial_year?: number | null;
  financial_years?: number[];
  prior_years_reliable?: boolean;
  metrics?: Record<string, MetricValue>;
}

/** Human labels. Deliberately plain: an accountant should recognise the line
 *  without learning our vocabulary. */
export const LINE_LABEL: Record<StandardLine, string> = {
  cash_and_banks: 'Cash and banks',
  receivables: 'Receivables',
  inventory: 'Inventory',
  other_current_assets: 'Other current assets',
  non_current_assets: 'Non-current assets',
  payables: 'Payables',
  short_term_debt: 'Short-term debt',
  other_current_liabilities: 'Other current liabilities',
  non_current_liabilities: 'Non-current liabilities',
  equity: 'Equity',
  revenue: 'Revenue',
  cogs: 'Cost of sales',
  opex: 'Operating expenses',
  other_income: 'Other income',
  other_expense: 'Other expenses (interest, tax, FX)',
  excluded: 'Not part of the statements'
};

export const LINE_GROUP: Record<StandardLine, string> = {
  cash_and_banks: 'Balance sheet — assets',
  receivables: 'Balance sheet — assets',
  inventory: 'Balance sheet — assets',
  other_current_assets: 'Balance sheet — assets',
  non_current_assets: 'Balance sheet — assets',
  payables: 'Balance sheet — liabilities and equity',
  short_term_debt: 'Balance sheet — liabilities and equity',
  other_current_liabilities: 'Balance sheet — liabilities and equity',
  non_current_liabilities: 'Balance sheet — liabilities and equity',
  equity: 'Balance sheet — liabilities and equity',
  revenue: 'Profit and loss',
  cogs: 'Profit and loss',
  opex: 'Profit and loss',
  other_income: 'Profit and loss',
  other_expense: 'Profit and loss',
  excluded: 'Excluded'
};

async function jsonFetch(url: string, init?: RequestInit) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiFinancialSettings(
  financialYear?: number | null
): Promise<FinancialSettings> {
  const qs = new URLSearchParams();
  if (financialYear) qs.set('financial_year', String(financialYear));
  const suffix = qs.toString() ? `?${qs}` : '';
  return jsonFetch(`/api/financial/settings${suffix}`);
}

export async function apiSaveRulings(rulings: Ruling[]): Promise<{
  saved: number;
  approval_lapsed: boolean;
  map_fingerprint: string;
}> {
  return jsonFetch('/api/financial/map', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rulings })
  });
}

export async function apiApproveMap(
  note?: string
): Promise<{ approved: boolean; map_fingerprint: string }> {
  return jsonFetch('/api/financial/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note })
  });
}

export async function apiFinancialMetrics(
  financialYear?: number | null
): Promise<FinancialMetrics> {
  const qs = new URLSearchParams();
  if (financialYear) qs.set('financial_year', String(financialYear));
  const suffix = qs.toString() ? `?${qs}` : '';
  return jsonFetch(`/api/financial/metrics${suffix}`);
}

/** Money as an accountant reads it, with the sign kept. */
export function formatMoney(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

export function formatMetric(m: MetricValue): string {
  if (m.value === undefined) return '—';
  switch (m.unit) {
    case 'percent':
      return `${m.value.toFixed(1)}%`;
    case 'ratio':
      return m.value.toFixed(2);
    case 'days':
      return `${Math.round(m.value)} days`;
    default:
      return formatMoney(m.value);
  }
}
